import React, { useState, useEffect } from 'react';
import { Users, Mail, Clock, ToggleLeft, ToggleRight, Send, RefreshCw, ChevronLeft, ChevronRight, Loader2, UserX, Bell } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminInactiveUsers = () => {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({ enabled: true, reminder_3day_enabled: true, reminder_14day_enabled: true, reminder_30day_enabled: true });
  const [users, setUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [daysFilter, setDaysFilter] = useState(14);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [histPage, setHistPage] = useState(1);
  const [histTotalPages, setHistTotalPages] = useState(1);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (activeTab === 'users') loadUsers(); }, [daysFilter, page, activeTab]);
  useEffect(() => { if (activeTab === 'history') loadHistory(); }, [histPage, activeTab]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [statsRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/inactive/stats`, { headers }),
        fetch(`${API_URL}/api/admin/inactive/settings`, { headers }),
      ]);
      const [s, sett] = await Promise.all([statsRes.json(), settingsRes.json()]);
      setStats(s);
      setSettings(sett);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/inactive/users?days=${daysFilter}&page=${page}&limit=20`, { headers });
      const data = await res.json();
      setUsers(data.users || []);
      setTotalPages(data.pages || 1);
    } catch (e) { console.error(e); }
  };

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/inactive/history?page=${histPage}&limit=30`, { headers });
      const data = await res.json();
      setHistory(data.records || []);
      setHistTotalPages(data.pages || 1);
    } catch (e) { console.error(e); }
  };

  const saveSettings = async (newSettings) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/inactive/settings`, { method: 'PUT', headers, body: JSON.stringify(newSettings) });
      if (!res.ok) throw new Error('Failed to save');
      const data = await res.json();
      setSettings(data);
      toast({ title: 'Settings saved' });
    } catch { toast({ title: 'Save failed', variant: 'destructive' }); }
  };

  const sendManual = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/inactive/send-manual`, { method: 'POST', headers, body: JSON.stringify({}) });
      if (!res.ok) throw new Error('Failed');
      toast({ title: 'Reminder emails triggered', description: 'All eligible inactive users will receive emails' });
      loadAll();
    } catch { toast({ title: 'Failed to trigger', variant: 'destructive' }); }
    finally { setSending(false); }
  };

  const toggle = (key) => {
    const updated = { ...settings, [key]: !settings[key] };
    setSettings(updated);
    saveSettings(updated);
  };

  const statCards = stats ? [
    { label: 'Active Users', value: stats.total_active_users, icon: Users, color: 'bg-blue-100 text-blue-600' },
    { label: 'Inactive 3+ Days', value: stats.inactive_3_days, icon: Clock, color: 'bg-teal-100 text-teal-600' },
    { label: 'Inactive 14+ Days', value: stats.inactive_14_days, icon: Clock, color: 'bg-amber-100 text-amber-600' },
    { label: 'Inactive 30+ Days', value: stats.inactive_30_days, icon: UserX, color: 'bg-red-100 text-red-600' },
    { label: 'Total Reminders Sent', value: stats.total_reminders_sent, icon: Mail, color: 'bg-green-100 text-green-600' },
    { label: '3-Day Sent', value: stats.reminders_3day_sent, icon: Bell, color: 'bg-cyan-100 text-cyan-600' },
    { label: '14-Day Sent', value: stats.reminders_14day_sent, icon: Bell, color: 'bg-pink-100 text-pink-600' },
    { label: '30-Day Sent', value: stats.reminders_30day_sent, icon: Bell, color: 'bg-purple-100 text-purple-600' },
  ] : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inactive User Reminders</h1>
          <p className="text-gray-500 text-sm">Re-engage users who haven't visited recently</p>
        </div>
        <button onClick={sendManual} disabled={sending || !settings.enabled}
          className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 disabled:opacity-50 text-white px-4 py-2 rounded-xl font-medium transition-colors"
          data-testid="send-reminders-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send Reminders Now
        </button>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-pink-500" /></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
          {statCards.map((card) => (
            <div key={card.label} className="bg-white rounded-xl p-4 shadow-sm">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${card.color}`}>
                <card.icon className="w-4 h-4" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{card.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{card.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Settings Panel */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Bell className="w-5 h-5 text-pink-500" /> Reminder Settings</h2>
        <div className="space-y-4">
          {[
            { key: 'enabled', label: 'Enable All Reminders', desc: 'Master toggle — disables all reminder emails when off' },
            { key: 'reminder_3day_enabled', label: '3-Day Reminder', desc: 'Send "Don\'t Miss Out" email after 3 days of inactivity' },
            { key: 'reminder_14day_enabled', label: '14-Day Reminder', desc: 'Send "We Miss You" email after 14 days of inactivity' },
            { key: 'reminder_30day_enabled', label: '30-Day Reminder', desc: 'Send "Your Account Is Waiting" email after 30 days' },
          ].map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
              <div>
                <p className="font-medium text-gray-900 text-sm">{label}</p>
                <p className="text-gray-500 text-xs mt-0.5">{desc}</p>
              </div>
              <button onClick={() => toggle(key)} className="text-gray-400 hover:text-pink-500 transition-colors" data-testid={`toggle-${key}`}>
                {settings[key]
                  ? <ToggleRight className="w-8 h-8 text-pink-500" />
                  : <ToggleLeft className="w-8 h-8 text-gray-400" />}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'users', label: 'Inactive Users' },
          { id: 'history', label: 'Email History' },
        ].map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-pink-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-bold text-gray-900 mb-4">How It Works</h3>
          <div className="space-y-4">
            {[
              { day: '3 Days', color: 'bg-teal-100 text-teal-700 border-teal-200', title: 'Early Nudge — "Don\'t Miss Out"', desc: 'Sent to approved users who have not logged in for 3 days. A light, friendly reminder about new members and matches. Re-sent every 10 days while the user stays inactive (until 14 days).' },
              { day: '14 Days', color: 'bg-amber-100 text-amber-700 border-amber-200', title: 'First Reminder — "We Miss You"', desc: 'Sent to approved users who have not logged in for 14 days. Includes their profile picture, completion %, and verification status.' },
              { day: '30 Days', color: 'bg-red-100 text-red-700 border-red-200', title: 'Second Reminder — "Your Account Is Waiting"', desc: 'Sent if the user has been inactive for 30+ days. A final nudge to return to the platform.' },
              { day: 'Login', color: 'bg-green-100 text-green-700 border-green-200', title: 'Reminders Stop Automatically', desc: 'As soon as the user logs in, no more reminders are sent. The 3/14/30-day clocks reset.' },
            ].map((item) => (
              <div key={item.day} className={`flex gap-4 p-4 rounded-xl border ${item.color}`}>
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm ${item.color}`}>{item.day}</div>
                <div>
                  <p className="font-semibold text-gray-900">{item.title}</p>
                  <p className="text-gray-600 text-sm mt-1">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
            <strong>Scheduled:</strong> Runs automatically every day at 10:00 AM UTC. Use "Send Reminders Now" to trigger manually.
          </div>
        </div>
      )}

      {/* Inactive Users Tab */}
      {activeTab === 'users' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <select value={daysFilter} onChange={(e) => { setDaysFilter(Number(e.target.value)); setPage(1); }}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300">
              <option value={3}>Inactive 3+ days</option>
              <option value={14}>Inactive 14+ days</option>
              <option value={30}>Inactive 30+ days</option>
              <option value={60}>Inactive 60+ days</option>
              <option value={90}>Inactive 90+ days</option>
            </select>
            <button onClick={loadUsers} className="p-2 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
              <RefreshCw className="w-4 h-4 text-gray-600" />
            </button>
          </div>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">User</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">Last Active</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">Days Inactive</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">Last Reminder</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-12 text-gray-400">No inactive users found</td></tr>
                ) : users.map((u) => (
                  <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full overflow-hidden bg-pink-100 flex-shrink-0">
                          {u.profile_picture
                            ? <img src={u.profile_picture} alt="" className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center text-pink-600 font-bold text-sm">{(u.name || u.email)[0].toUpperCase()}</div>}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{u.name || '—'}</p>
                          <p className="text-gray-400 text-xs">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{u.last_active ? new Date(u.last_active).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.days_inactive >= 30 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {u.days_inactive} days
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {u.last_reminder_type ? (
                        <><span className="font-medium">{u.last_reminder_type}</span><br />{u.last_reminder_sent ? new Date(u.last_reminder_sent).toLocaleDateString() : ''}</>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {u.opt_out
                        ? <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-500">Unsubscribed</span>
                        : <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">Active</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="flex items-center gap-1 text-sm text-gray-600 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /> Prev</button>
                <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="flex items-center gap-1 text-sm text-gray-600 disabled:opacity-40">Next <ChevronRight className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-gray-500 font-medium">User</th>
                <th className="text-left px-4 py-3 text-gray-500 font-medium">Type</th>
                <th className="text-left px-4 py-3 text-gray-500 font-medium">Sent At</th>
                <th className="text-left px-4 py-3 text-gray-500 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-12 text-gray-400">No reminder history yet</td></tr>
              ) : history.map((r) => (
                <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{r.user_name || '—'}</p>
                    <p className="text-gray-400 text-xs">{r.user_email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${r.reminder_type === '3day' ? 'bg-teal-100 text-teal-700' : r.reminder_type === '14day' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                      {r.reminder_type === '3day' ? '3-Day' : r.reminder_type === '14day' ? '14-Day' : '30-Day'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">{r.status || 'sent'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {histTotalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <button onClick={() => setHistPage(p => Math.max(1, p - 1))} disabled={histPage === 1} className="flex items-center gap-1 text-sm text-gray-600 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /> Prev</button>
              <span className="text-sm text-gray-500">Page {histPage} of {histTotalPages}</span>
              <button onClick={() => setHistPage(p => Math.min(histTotalPages, p + 1))} disabled={histPage === histTotalPages} className="flex items-center gap-1 text-sm text-gray-600 disabled:opacity-40">Next <ChevronRight className="w-4 h-4" /></button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminInactiveUsers;
